import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useRoutedNavigation } from "presentation/hooks/useRoutedNavigation";
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Chip,
  Button,
  Alert,
  Divider,
  Card,
  CardContent,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DownloadIcon from '@mui/icons-material/Download';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import DescriptionIcon from '@mui/icons-material/Description';
import CategoryIcon from '@mui/icons-material/Category';
import ArticleIcon from '@mui/icons-material/Article';
import PageContainer from '../components/shared/pageContainer';
import PageHeader from '../components/shared/page-header';
import { DetailSkeleton } from '../components/shared/skeletons';
import { usePublicationDetailController } from './context/publication-detail-controller';
import { usePublicationDetailState } from './context/publication-detail-context';

export function PublicationDetailComponent() {
  const { id } = useParams<{ id: string }>();
  const { navigateTo: navigate } = useRoutedNavigation();
  const { loadDetail } = usePublicationDetailController();
  const { detail, loading, error } = usePublicationDetailState();

  useEffect(() => {
    if (id) {
      loadDetail(Number(id));
    }
  }, [id]);

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  };

  const handleDownload = () => {
    if (detail?.fileDownloadUrl) {
      const link = document.createElement('a');
      link.href = detail.fileDownloadUrl;
      link.download = `publicacao-${detail.id}.pdf`;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleViewPDF = () => {
    if (detail?.fileViewUrl) {
      window.open(detail.fileViewUrl, '_blank');
    }
  };

  const handleDownloadDailyOfficial = () => {
    if (detail?.dailyFileDownloadUrl) {
      const link = document.createElement('a');
      link.href = detail.dailyFileDownloadUrl;
      link.download = `diario-oficial-edicao-${detail.editionNumber}.pdf`;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleViewDailyOfficial = () => {
    if (detail?.dailyFileViewUrl) {
      window.open(detail.dailyFileViewUrl, '_blank');
    }
  };

  const truncateText = (text: string | undefined, maxLength: number = 60): string => {
    if (!text) return `Publicação #${id}`;
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  if (loading) {
    return <DetailSkeleton hasImage={false} hasSections={2} />;
  }

  if (error || !detail) {
    return (
      <PageContainer>
        <Container maxWidth="xl">
          <Alert severity="error" sx={{ mt: 2 }}>
            {error || 'Publicação não encontrada'}
          </Alert>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/publicacoes')}
            sx={{ mt: 2 }}
          >
            Voltar para listagem
          </Button>
        </Container>
      </PageContainer>
    );
  }

  const displayTitle = truncateText(detail.description, 60);

  return (
    <PageContainer>
      <Container maxWidth="xl">
        <PageHeader
          title={displayTitle}
          subtitle="Detalhes da Publicação"
          breadcrumbs={[
            { label: 'Publicações do Diário Oficial', href: '/publicacoes' },
            { label: displayTitle },
          ]}
        />

        {/* Informações Principais */}
        <Paper elevation={3} sx={{ p: 4, mb: 3, borderRadius: 2 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <CalendarTodayIcon color="primary" />
                <Typography variant="h6" color="primary">
                  Informações da Publicação
                </Typography>
              </Box>
              <Box>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <strong>Data de Publicação:</strong> {formatDate(detail.publicationDate)}
                </Typography>
                {detail.sanctionDate && (
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    <strong>Data de Sanção:</strong> {formatDate(detail.sanctionDate)}
                  </Typography>
                )}
                {detail.numberingCode && (
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    <strong>Código de Numeração:</strong> {detail.numberingCode}
                  </Typography>
                )}
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <strong>Edição:</strong> {detail.editionNumber}/{detail.year}
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <strong>Páginas:</strong> {detail.initialPage} - {detail.finalPage}
                </Typography>
                {detail.isRepublication && (
                  <Box sx={{ mt: 1 }}>
                    <Chip label="Republicação" color="warning" size="small" />
                  </Box>
                )}
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <CategoryIcon color="primary" />
                <Typography variant="h6" color="primary">
                  Categorização
                </Typography>
              </Box>
              <Box>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <strong>Categoria:</strong> {detail.category || '-'}
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <strong>Subcategoria:</strong> {detail.subCategory || '-'}
                </Typography>
                {detail.publishingAgency && (
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    <strong>Órgão Publicador:</strong> {detail.publishingAgency}
                  </Typography>
                )}
                {detail.publishingVehicle && (
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    <strong>Veículo de Publicação:</strong> {detail.publishingVehicle}
                  </Typography>
                )}
                {detail.tenderCommission && (
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    <strong>Comissão de Licitação:</strong> {detail.tenderCommission}
                  </Typography>
                )}
                {detail.responsiblePresident && (
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    <strong>Presidente Responsável:</strong> {detail.responsiblePresident}
                  </Typography>
                )}
              </Box>
            </Grid>
          </Grid>

          {detail.observation && (
            <>
              <Divider sx={{ my: 3 }} />
              <Box>
                <Typography variant="h6" color="primary" sx={{ mb: 2 }}>
                  Observações
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {detail.observation}
                </Typography>
              </Box>
            </>
          )}
        </Paper>

        {/* Ações - Publicação Específica */}
        <Card elevation={3} sx={{ mb: 3, borderRadius: 2 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <ArticleIcon color="primary" />
              <Typography variant="h6" color="primary">
                Documento da Publicação
              </Typography>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Button
                  variant="contained"
                  startIcon={<PictureAsPdfIcon />}
                  onClick={handleViewPDF}
                  disabled={!detail.fileViewUrl}
                  fullWidth
                  size="large"
                >
                  Visualizar Publicação
                </Button>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={handleDownload}
                  disabled={!detail.fileDownloadUrl}
                  fullWidth
                  size="large"
                >
                  Baixar Publicação
                </Button>
              </Grid>
            </Grid>
            {detail.publicationAuthenticityCode && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                <strong>Código de Autenticidade da Publicação:</strong> {detail.publicationAuthenticityCode}
              </Typography>
            )}
          </CardContent>
        </Card>

        {/* Ações - Diário Oficial Completo */}
        {(detail.dailyFileViewUrl || detail.dailyFileDownloadUrl) && (
          <Card elevation={3} sx={{ mb: 3, borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <DescriptionIcon color="secondary" />
                <Typography variant="h6" color="secondary">
                  Diário Oficial Completo (Edição {detail.editionNumber}/{detail.year})
                </Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Button
                    variant="contained"
                    color="secondary"
                    startIcon={<PictureAsPdfIcon />}
                    onClick={handleViewDailyOfficial}
                    disabled={!detail.dailyFileViewUrl}
                    fullWidth
                    size="large"
                  >
                    Visualizar Diário Completo
                  </Button>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Button
                    variant="outlined"
                    color="secondary"
                    startIcon={<DownloadIcon />}
                    onClick={handleDownloadDailyOfficial}
                    disabled={!detail.dailyFileDownloadUrl}
                    fullWidth
                    size="large"
                  >
                    Baixar Diário Completo
                  </Button>
                </Grid>
              </Grid>
              {detail.dailyAuthenticityCode && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  <strong>Código de Autenticidade do Diário:</strong> {detail.dailyAuthenticityCode}
                </Typography>
              )}
            </CardContent>
          </Card>
        )}

        {/* Botão de voltar - centralizado no final */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            mt: 4,
            mb: 3,
          }}
        >
          <Button
            variant="contained"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/publicacoes')}
            size="large"
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              px: 4,
              py: 1.5,
            }}
          >
            Voltar para Publicações
          </Button>
        </Box>
      </Container>
    </PageContainer>
  );
}
